import React, { useEffect, useState, useRef } from 'react';
import { XIcon, SettingsIcon, BarChart2Icon, PlayIcon, AlertCircleIcon, RefreshCwIcon, BotIcon, SendIcon, MicIcon } from 'lucide-react';
import { usePreventScroll } from '../../../hooks/usePreventScroll';
import { toast } from 'sonner';
import { ERROR_MESSAGES } from '../../../config/error-messages';
import { useErrorBoundary } from '../../../hooks/useErrorBoundary';
import { FormField } from '../../common/FormField';
interface Message {
  role: 'user' | 'bot';
  content: string;
  timestamp: string;
}
interface Props {
  isOpen: boolean;
  onClose: () => void;
  chatbot: {
    id: string;
    name: string;
    personality: string;
    status: 'active' | 'inactive';
  };
  onUpdate: (data: any) => void;
  initialTab?: 'settings' | 'analytics' | 'test';
}
export const ChatbotSettingsModal = ({
  isOpen,
  onClose,
  chatbot,
  onUpdate,
  initialTab = 'settings'
}: Props) => {
  usePreventScroll(isOpen);
  // Update activeTab when initialTab changes
  const [activeTab, setActiveTab] = useState(initialTab);
  // Add useEffect to sync activeTab with initialTab
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  const [formData, setFormData] = useState({
    name: chatbot.name,
    personality: chatbot.personality,
    status: chatbot.status,
    maxTokens: 1500,
    temperature: 0.7,
    topP: 0.9
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [messages, setMessages] = useState<Message[]>([{
    role: 'bot',
    content: `Hi! I'm ${chatbot.name}. This is a test chat to see how I respond based on my current settings. How can I help you today?`,
    timestamp: new Date().toLocaleTimeString()
  }]);
  const [inputMessage, setInputMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    if (activeTab === 'test') {
      inputRef.current?.focus();
      scrollToBottom();
    }
  }, [activeTab, messages]);
  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;
    const newMessage: Message = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toLocaleTimeString()
    };
    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');
    // Simulate bot response with current settings
    setTimeout(() => {
      const botResponse: Message = {
        role: 'bot',
        content: `This is a test response using the following settings:\n\nTemperature: ${formData.temperature}\nMax Tokens: ${formData.maxTokens}\nTop P: ${formData.topP}\n\nIn a real implementation, this would be processed through the AI model with the configured personality: "${formData.personality}"`,
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages(prev => [...prev, botResponse]);
    }, 1000);
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const handleStartRecording = () => {
    setIsRecording(true);
    // In a real implementation, this would handle audio recording
    setTimeout(() => {
      setIsRecording(false);
      setInputMessage('This is a simulated voice message...');
    }, 2000);
  };
  const tabs = [{
    id: 'settings',
    label: 'Settings',
    icon: SettingsIcon
  }, {
    id: 'analytics',
    label: 'Analytics',
    icon: BarChart2Icon
  }, {
    id: 'test',
    label: 'Test Chat',
    icon: PlayIcon
  }];
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.personality.trim()) {
      newErrors.personality = 'Personality description is required';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    return true;
  };
  const [isLoading, setIsLoading] = useState(false);
  const [{
    hasError,
    error
  }, handleError, resetError] = useErrorBoundary();
  const handleSubmit = async () => {
    try {
      if (validateForm()) {
        setIsLoading(true);
        await onUpdate(formData);
        toast.success('Chatbot settings updated successfully');
        onClose();
      }
    } catch (error) {
      handleError(error as Error);
      toast.error(ERROR_MESSAGES.api.serverError);
    } finally {
      setIsLoading(false);
    }
  };
  if (hasError) {
    return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-835">
        <div className="bg-surface-light border border-surface-border rounded-none p-6 max-w-lg w-full" data-id="element-836">
          <div className="flex items-start gap-4" data-id="element-837">
            <div className="p-3 bg-status-error/10 rounded-none" data-id="element-838">
              <AlertCircleIcon size={24} className="text-status-error" data-id="element-839" />
            </div>
            <div className="flex-1" data-id="element-840">
              <h1 className="text-lg font-medium text-white font-mono mb-2" data-id="element-841">
                Failed to Update Settings
              </h1>
              <div className="bg-surface p-4 rounded-none border border-surface-border mb-4" data-id="element-842">
                <code className="text-sm text-gray-400 font-mono" data-id="element-843">
                  {error?.message || ERROR_MESSAGES.api.serverError}
                </code>
              </div>
              <div className="flex gap-3" data-id="element-844">
                <button onClick={resetError} className="flex items-center gap-2 px-4 py-2 bg-accent 
                           hover:bg-accent-light text-white rounded-none transition-colors" data-id="element-845">
                  <RefreshCwIcon size={16} data-id="element-846" />
                  <span data-id="element-847">Try Again</span>
                </button>
                <button onClick={onClose} className="px-4 py-2 border border-surface-border text-gray-400 
                           hover:text-white rounded-none transition-colors" data-id="element-848">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4" data-id="element-849">
      <div className="bg-surface-light border border-surface-border rounded-none w-full max-w-2xl flex flex-col max-h-[90vh]" data-id="element-850">
        {/* Fixed Header */}
        <div className="shrink-0 border-b border-surface-border" data-id="element-851">
          {/* Tabs Header */}
          <div className="px-6 py-4 flex items-center justify-between" data-id="element-852">
            <h2 className="text-lg font-medium text-white font-mono" data-id="element-853">
              {chatbot.name} Settings
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300 transition-colors" data-id="element-854">
              <XIcon size={20} data-id="element-855" />
            </button>
          </div>
          {/* Tabs */}
          <div className="border-t border-surface-border" data-id="element-856">
            <div className="flex" data-id="element-857">
              {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as typeof activeTab)} className={`
                    flex items-center gap-2 px-6 py-3 text-sm font-mono uppercase tracking-wide
                    ${activeTab === tab.id ? 'text-accent-light border-b-2 border-accent' : 'text-gray-400 hover:text-gray-300'}
                  `} data-id="element-858">
                  <tab.icon size={16} data-id="element-859" />
                  {tab.label}
                </button>)}
            </div>
          </div>
        </div>
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto min-h-0" data-id="element-860">
          <div className="p-6" data-id="element-861">
            {activeTab === 'settings' && <div className="space-y-6" data-id="element-862">
                <FormField label="Chatbot Name" type="text" value={formData.name} onChange={value => setFormData(prev => ({
              ...prev,
              name: value
            }))} error={errors.name} required data-id="element-863" />
                <FormField label="Personality Description" type="textarea" value={formData.personality} onChange={value => setFormData(prev => ({
              ...prev,
              personality: value
            }))} error={errors.personality} required tooltip="Define how your chatbot should interact with users" data-id="element-864" />
                <div className="space-y-6 bg-surface p-4 border border-surface-border" data-id="element-865">
                  <h3 className="text-sm font-medium text-white font-mono sticky top-0 bg-surface pb-4" data-id="element-866">
                    Model Parameters
                  </h3>
                  <div className="space-y-6" data-id="element-867">
                    {' '}
                    {/* Increased spacing between parameters */}
                    <div className="space-y-4" data-id="element-868">
                      <div data-id="element-869">
                        <label className="block text-sm text-gray-400 mb-3" data-id="element-870">
                          Temperature
                        </label>
                        <div className="flex items-center gap-4" data-id="element-871">
                          <input type="range" min="0" max="1" step="0.1" value={formData.temperature} onChange={e => setFormData(prev => ({
                        ...prev,
                        temperature: parseFloat(e.target.value)
                      }))} className="flex-1" data-id="element-872" />
                          <span className="text-sm text-gray-400 w-12 text-right" data-id="element-873">
                            {formData.temperature}
                          </span>
                        </div>
                      </div>
                      <div data-id="element-874">
                        <label className="block text-sm text-gray-400 mb-3" data-id="element-875">
                          Max Tokens
                        </label>
                        <input type="number" value={formData.maxTokens} onChange={e => setFormData(prev => ({
                      ...prev,
                      maxTokens: parseInt(e.target.value)
                    }))} className="w-32 px-3 py-2 bg-surface border border-surface-border rounded-none text-white" data-id="element-876" />
                      </div>
                      <div data-id="element-877">
                        <label className="block text-sm text-gray-400 mb-3" data-id="element-878">
                          Top P
                        </label>
                        <div className="flex items-center gap-4" data-id="element-879">
                          <input type="range" min="0" max="1" step="0.1" value={formData.topP} onChange={e => setFormData(prev => ({
                        ...prev,
                        topP: parseFloat(e.target.value)
                      }))} className="flex-1" data-id="element-880" />
                          <span className="text-sm text-gray-400 w-12 text-right" data-id="element-881">
                            {formData.topP}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {Object.keys(errors).length > 0 && <div className="flex items-start gap-2 text-status-error text-sm" data-id="element-882">
                    <AlertCircleIcon size={16} className="mt-0.5" data-id="element-883" />
                    <p data-id="element-884">Please fix the errors above before saving</p>
                  </div>}
              </div>}
            {activeTab === 'analytics' && <div className="space-y-6" data-id="element-885">
                <p className="text-gray-400" data-id="element-886">Analytics coming soon...</p>
              </div>}
            {activeTab === 'test' && <div className="space-y-6" data-id="element-887">
                <div className="bg-surface border border-surface-border rounded-none p-4" data-id="element-888">
                  <p className="text-sm text-gray-400 mb-2" data-id="element-889">
                    Test the chatbot with its current settings:
                  </p>
                  <div className="text-xs text-gray-400 space-y-1" data-id="element-890">
                    <p data-id="element-891">• Temperature: {formData.temperature}</p>
                    <p data-id="element-892">• Max Tokens: {formData.maxTokens}</p>
                    <p data-id="element-893">• Top P: {formData.topP}</p>
                  </div>
                </div>
                {/* Chat Container */}
                <div className="h-[400px] overflow-y-auto bg-surface border border-surface-border rounded-none" data-id="element-894">
                  <div className="p-4 space-y-6" data-id="element-895">
                    {messages.map((message, index) => <div key={index} className={`flex items-start gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`} data-id="element-896">
                        {message.role === 'bot' && <div className="w-8 h-8 rounded-none bg-accent/10 flex items-center justify-center shrink-0" data-id="element-897">
                            <BotIcon size={16} className="text-accent-light" data-id="element-898" />
                          </div>}
                        <div className={`
                            flex-1 max-w-[80%] p-4 rounded-none
                            ${message.role === 'user' ? 'bg-accent text-white' : 'bg-surface-mid border border-surface-border text-gray-100'}
                          `} data-id="element-899">
                          <p className="text-sm whitespace-pre-wrap" data-id="element-900">
                            {message.content}
                          </p>
                          <p className="text-xs mt-2 opacity-60" data-id="element-901">
                            {message.timestamp}
                          </p>
                        </div>
                      </div>)}
                    <div ref={messagesEndRef} data-id="element-902" />
                  </div>
                </div>
                {/* Input Area */}
                <div className="flex items-end gap-3" data-id="element-903">
                  <textarea ref={inputRef} value={inputMessage} onChange={e => setInputMessage(e.target.value)} onKeyPress={handleKeyPress} placeholder="Type your message..." className="flex-1 px-4 py-2 bg-surface-light border border-surface-border rounded-none 
                             text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 
                             focus:ring-accent focus:border-transparent" rows={1} style={{
                minHeight: '40px',
                maxHeight: '120px'
              }} data-id="element-904" />
                  <button onClick={() => handleStartRecording()} className={`p-2 rounded-none transition-colors ${isRecording ? 'bg-status-error text-white' : 'text-gray-400 hover:text-accent-light hover:bg-surface'}`} data-id="element-905">
                    <MicIcon size={20} className={isRecording ? 'animate-pulse' : ''} data-id="element-906" />
                  </button>
                  <button onClick={handleSendMessage} disabled={!inputMessage.trim()} className="px-4 py-2 bg-accent hover:bg-accent-light text-white rounded-none 
                             transition-colors disabled:opacity-50 disabled:cursor-not-allowed 
                             flex items-center gap-2 shrink-0" data-id="element-907">
                    <SendIcon size={16} data-id="element-908" />
                    <span className="font-mono text-sm" data-id="element-909">Send</span>
                  </button>
                </div>
              </div>}
          </div>
        </div>
        {/* Fixed Footer - Only show for Settings tab */}
        {activeTab === 'settings' && <div className="shrink-0 px-6 py-4 border-t border-surface-border flex justify-between" data-id="element-910">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-gray-300 transition-colors" data-id="element-911">
              Cancel
            </button>
            <button onClick={handleSubmit} className="px-6 py-2 bg-accent hover:bg-accent-light text-white text-sm 
                     font-mono uppercase tracking-wide rounded-none transition-colors" data-id="element-912">
              Save Changes
            </button>
          </div>}
      </div>
    </div>;
};